import { AusState } from "@aus/Store/AusState";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getPageLinkByNodeId, LinkModel, mapLinks } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography from "@insite/mobius/Typography/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

const enum fields {
    rightTextContent = "rightTextContent",
    links = "links",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.rightTextContent]: string;
        [fields.links]: LinkModel[];
    };
    extendedStyles?: HeaderTopLinksStyles;
}

const mapStateToProps = (state: AusState, ownProps: OwnProps) => {
    const links = mapLinks<LinkModel, { openInNewWindow: boolean; overriddenTitle: string }>(
        state,
        ownProps.fields.links,
        widgetLink => ({
            openInNewWindow: widgetLink.fields.openInNewWindow,
            overriddenTitle: widgetLink.fields.overriddenTitle,
        }),
    );
    return {
        links,
        isSecondaryHeader: state.headerPage.isSecondaryHeader,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface HeaderTopLinksStyles {
    linkListWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    container?: InjectableCss;
    leftContent?: InjectableCss;
    rightContent?: InjectableCss;
    innerContainer?: InjectableCss;
}

export const headerTopLinksStyles: HeaderTopLinksStyles = {
    linkListWrapper: {
        css: css`
            text-transform: uppercase;
            padding: 10px 0;
        `,
    },
    linkWrapper: {
        css: css`
            padding: 1px 0 0 18px;
        `,
    },
    container: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding: 7px 0 7px 10px;
            background-color: ${getColor("primary.main")};
            align-items: center;
            max-height: 120px;
            opacity: 1;
            visibility: visible;
            transition: padding 1s;
            border-bottom: 4px solid #616161;
        `,
    },
    leftContent: {
        css: css`
            display: flex;
            justify-content: left;
            flex: 0 0 50%;
            align-items: center;
        `,
    },
    rightContent: {
        css: css`
            display: flex;
            justify-content: flex-end;
            flex: 0 0 50%;
            align-items: center;
            padding-right: 16px;
        `,
    },
    innerContainer: {
        css: css`
            max-width: 1700px;
            margin: auto;
            display: flex;
            width: 100%;
        `,
    },
};

const HeaderTopLinks: React.FC<Props> = ({ id, fields, links, extendedStyles, isSecondaryHeader }) => {
    const [styles] = React.useState(() => mergeToNew(headerTopLinksStyles, extendedStyles));

    let containerStyles = styles.container;
    if (isSecondaryHeader) {
        containerStyles = {
            css: css`
                display: flex;
                justify-content: space-between;
                padding: 5px 0 5px 10px;
                background-color: ${getColor("primary.main")};
                align-items: center;
                transition: padding 1s;
            `,
        };
    }

    return (
        <StyledWrapper {...containerStyles}>
            <StyledWrapper {...styles.innerContainer}>
                <StyledWrapper {...styles.leftContent}>
                    <Zone contentId={id} zoneName="Content"></Zone>
                </StyledWrapper>
                <StyledWrapper {...styles.rightContent}>
                    <Typography color="primary.contrast">{fields.rightTextContent}</Typography>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(HeaderTopLinks),
    definition: {
        group: "Basic",
        icon: "LinkList",
        fieldDefinitions: [
            {
                name: fields.rightTextContent,
                displayName: "Right Text Content",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                tooltip: "Enter text",
                placeholder: "Enter text",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return link?.title;
                    }
                    if (type === "Category") {
                        return value; // TODO ISC-10781 make this work
                    }
                    if (type === "Url") {
                        return value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        displayName: "Title",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
